import React, { lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ProtectedRoute } from "../components/routes/ProtectedRoute";
import { useAppSelector } from "../app/hooks";
import { RootState } from "../app/store";
import { SpinnerBig } from "../components/Loaders/MainLoader/Spinner/SpinnerBig";
import { useAccess } from "../hooks/useAccess";

const MainContainer = lazy(() =>
    import("../components/pages/MainContainer/MainContainer").then((module) => ({
        default: module.MainContainer,
    }))
);


const Cards = lazy(() => import("../components/pages/Cards/Cards").then((module) => ({ default: module.Cards })));
const MakeOrder = lazy(() => import("../components/pages/MakeOrder/MakeOrder").then((module) => ({ default: module.MakeOrder })));
const UserWrapper = lazy(() => import("../components/pages/UserWrapper/UserWrapper").then((module) => ({ default: module.UserWrapper })));
const Services = lazy(() => import("../components/pages/User/Services").then((module) => ({ default: module.Services })));
const Login = lazy(() => import("../components/pages/Login/Login").then((module) => ({ default: module.Login })));
const ActivationPage = lazy(() => import("../components/pages/ActivationPage/ActivationPage").then((module) => ({ default: module.ActivationPage })));
const Packages = lazy(() => import("../components/pages/Packages/Packages").then((module) => ({ default: module.Packages })));

const Dashboard = lazy(() => import("../components/pages/DashboardWrapper/DashboardWrapper").then((module) => ({ default: module.DashboardWrapper })));
const DashboardEditPage = lazy(() => import("../components/forms/EditData/EditData").then((module) => ({ default: module.EditData })));
const DashboardEmployees = lazy(() => import("../components/sections/Dashboard/Employees").then((module) => ({ default: module.Employees })));
const DashboardBranches = lazy(() => import("../components/sections/Dashboard/Branches").then((module) => ({ default: module.Branches })));
const DashboardRatingsContent = lazy(() => import("../components/sections/Dashboard/RatingsContent").then((module) => ({ default: module.RatingsContent })));
const DashboardRatings = lazy(() => import("../components/sections/Dashboard/Ratings").then((module) => ({ default: module.Ratings })));
const DashboardServicesContent = lazy(() => import("../components/sections/Dashboard/ServicesContent").then((module) => ({ default: module.ServicesContent })));
const DashboardAnalyticsContent = lazy(() => import("../components/sections/Dashboard/AnalyticsContent").then((module) => ({ default: module.AnalyticsContent })));
const DashboardAnalytics = lazy(() => import("../components/sections/Dashboard/Analytics").then((module) => ({ default: module.Analytics })));
const DashboardContent = lazy(() => import("../components/sections/Dashboard/Content").then((module) => ({ default: module.Content })));
const DashboardSettings = lazy(() => import("../components/sections/Dashboard/Settings").then((module) => ({ default: module.Settings })));
const DashboardSubscription = lazy(() => import("../components/sections/Dashboard/SubscriptionPage").then((module) => ({ default: module.SubscriptionPage })));
const DashboardSupport = lazy(() => import("../components/sections/Dashboard/Support").then((module) => ({ default: module.Support })));

const PageNotFound = lazy(() => import("../components/pages/PageNotFound/PageNotFound").then((module) => ({ default: module.PageNotFound })));
const Privacy = lazy(() => import("../components/sections/Privacy/Privacy").then((module) => ({ default: module.Privacy })));
const PrivacyHy = lazy(() => import("../components/sections/Privacy/PrivacyHy").then((module) => ({ default: module.PrivacyHy })));
const PrivacyRu = lazy(() => import("../components/sections/Privacy/PrivacyRu").then((module) => ({ default: module.PrivacyRu })));
const Terms = lazy(() => import("../components/sections/Privacy/Terms").then((module) => ({ default: module.Terms })));
const TermsHy = lazy(() => import("../components/sections/Privacy/TermsHy").then((module) => ({ default: module.TermsHy })));
const TermsRu = lazy(() => import("../components/sections/Privacy/TermsRu").then((module) => ({ default: module.TermsRu })));
const PaymentCheckout = lazy(() => import("../components/pages/PaymentCheckout/PaymentCheckout").then((module) => ({ default: module.PaymentCheckout })));
const PaymentResult = lazy(() => import("../components/pages/PaymentResult/PaymentResult").then((module) => ({ default: module.PaymentResult })));
const SubscriptionCheckout = lazy(() => import("../components/pages/SubscriptionCheckout/SubscriptionCheckout").then((module) => ({ default: module.SubscriptionCheckout })));

export const PageRoutes = () => {

    const { data: userData, loading: userLoading } = useAppSelector(
        (state: RootState) => state.userMeta
    );
    const location = useLocation();

    const {
        hasBusinessFeatures: isPlanHigh,
        hasEnterpriseFeatures: isPlanOnlyEnterprise,
    } = useAccess();
    const isDashboard = location.pathname.includes('/dashboard');

    if (userLoading && isDashboard) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <SpinnerBig />
            </div>
        );
    }

    return (
        <Routes>
            <Route path="/" element={<MainContainer />} />
            {/* <Route path="/templates" element={<Templates />}></Route> */}
            <Route path="/cards" element={<Cards />}></Route>
            {!userData && (
                <Route path="/place-order" element={<MakeOrder />}></Route>
            )}
            <Route path="/card/:slug" element={<UserWrapper />}>
                <Route index element={null} />
                <Route path="services" element={<Services />} />
            </Route>
            <Route path="/login" element={<Login />}></Route>
            <Route path="/activate" element={<ActivationPage />}></Route>
            <Route path="/privacy-policy" element={<PrivacyHy />}></Route>
            <Route path="/privacy-policy/ru" element={<PrivacyRu />}></Route>
            <Route path="/privacy-policy/en" element={<Privacy />}></Route>
            <Route path="/terms-and-conditions" element={<TermsHy />}></Route>
            <Route path="/terms-and-conditions/ru" element={<TermsRu />}></Route>
            <Route path="/terms-and-conditions/en" element={<Terms />}></Route>
            <Route path="/packages" element={<Packages />}></Route>
            <Route path="/payment/checkout" element={<ProtectedRoute><PaymentCheckout /></ProtectedRoute>} />
            <Route path="/payment/result" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
            <Route path="/subscription/checkout" element={<ProtectedRoute><SubscriptionCheckout /></ProtectedRoute>} />
            <Route path="/dashboard" element={<Dashboard />}>
                <Route index element={<DashboardContent />} />
                <Route path="cards" element={<DashboardContent />} />
                <Route path="cards/:id" element={<DashboardEditPage />} />
                {isPlanHigh && (
                    <>
                        <Route path="branches" element={<DashboardBranches />} />
                        <Route path="employees" element={<DashboardEmployees />} />
                        <Route path="employees/:id" element={<DashboardEditPage />} />
                        <Route path="services" element={<DashboardServicesContent />} />
                    </>
                )}

                <Route path="analytics" element={<DashboardAnalyticsContent />} />
                <Route path="analytics/:id" element={<DashboardAnalytics />} />
                <Route path="settings" element={<DashboardSettings />} />
                <Route path="subscription" element={<DashboardSubscription />} />
                <Route path="support" element={<DashboardSupport />} />
                {isPlanOnlyEnterprise && (
                    <>
                        <Route path="ratings" element={<DashboardRatingsContent />} />
                        <Route path="ratings/:id" element={<DashboardRatings />} />
                    </>
                )}
            </Route>
            <Route path="*" element={<PageNotFound />} />
        </Routes >
    );
};
